import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FYLKER_MED_BYER, 
  FYLKER, 
  getByFromCoords, 
  getFylkeFromCoords
} from '../data/norwegianCities';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const LocationSelector = ({ 
  selected, 
  onSelect, 
  selectedBy,
  onBySelect,
  showAll = false, 
  horizontal = false, 
  showLocationButton = false 
}) => {
  const [sortedFylker, setSortedFylker] = useState(FYLKER);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState(null);

  // Hent byer for valgt fylke
  const currentFylke = selected ? FYLKER_MED_BYER.find(f => f.id === selected) : null;
  const byer = currentFylke?.byer || [];

  useEffect(() => {
    // Fetch job counts to sort fylker
    const fetchJobCounts = async () => {
      try {
        const response = await axios.get(`${API_URL}/jobs/stats/count-by-fylke`);
        if (response.data && response.data.length > 0) {
          const countMap = {};
          response.data.forEach(item => {
            countMap[item.fylke] = item.count;
          });
          
          // Sort FYLKER by job count (descending)
          const sorted = [...FYLKER].sort((a, b) => {
            const countA = countMap[a.id] || 0;
            const countB = countMap[b.id] || 0;
            return countB - countA;
          });
          setSortedFylker(sorted);
        }
      } catch (error) {
        // Use default order if API fails
        console.log('Using default fylke order');
      }
    };

    if (horizontal) {
      fetchJobCounts();
    }
  }, [horizontal]);

  const handleUseMyLocation = () => {
    setLocating(true);
    setLocationError(null);

    if (!navigator.geolocation) {
      // Fallback to IP-based location
      fetchIPLocation();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const result = getByFromCoords(latitude, longitude);
        if (result) {
          onSelect(result.fylkeId);
          if (onBySelect) {
            setTimeout(() => onBySelect(result.byId), 100);
          }
        } else {
          const fylke = getFylkeFromCoords(latitude, longitude);
          onSelect(fylke);
        }
        setLocating(false);
      },
      (error) => {
        console.log('Geolocation error, trying IP fallback:', error);
        // Fallback to IP-based location
        fetchIPLocation();
      },
      { timeout: 5000 }
    );
  };

  const fetchIPLocation = async () => {
    try {
      // Use a free IP geolocation service
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      
      if (data.latitude && data.longitude) {
        const result = getByFromCoords(data.latitude, data.longitude);
        if (result) {
          onSelect(result.fylkeId);
          if (onBySelect) {
            setTimeout(() => onBySelect(result.byId), 100);
          }
        } else {
          const fylke = getFylkeFromCoords(data.latitude, data.longitude);
          onSelect(fylke);
        }
      } else {
        setLocationError('Kunne ikke finne posisjonen din');
      }
    } catch (error) {
      setLocationError('Kunne ikke finne posisjonen din');
    } finally {
      setLocating(false);
    }
  };

  if (horizontal) {
    return (
      <div className="w-full">
        <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
          <div className="flex gap-2 pb-2" style={{ width: 'max-content' }}>
            {showLocationButton && (
              <button
                onClick={handleUseMyLocation}
                disabled={locating}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  locating
                    ? 'bg-slate-100 text-slate-400'
                    : 'bg-gradient-to-r from-[#1E3A5F] to-[#0A1929] text-white shadow-md hover:shadow-lg'
                }`}
              >
                <span className={`material-symbols-outlined text-lg ${locating ? 'animate-pulse' : ''}`}>
                  {locating ? 'location_searching' : 'my_location'}
                </span>
                {locating ? 'Finner...' : 'Min posisjon'}
              </button>
            )}
            
            {showAll && (
              <button
                onClick={() => {
                  onSelect(null);
                  if (onBySelect) onBySelect(null);
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  selected === null
                    ? 'bg-[#0A1929] text-white shadow-md'
                    : 'bg-white text-[#0A1929] border border-slate-200 hover:border-[#1E3A5F] hover:bg-slate-50'
                }`}
              >
                Hele Norge
              </button>
            )}
            
            {sortedFylker.map((fylke) => (
              <button
                key={fylke.id}
                onClick={() => {
                  onSelect(fylke.id);
                  if (onBySelect) onBySelect(null);
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  selected === fylke.id
                    ? 'bg-[#0A1929] text-white shadow-md'
                    : 'bg-white text-[#0A1929] border border-slate-200 hover:border-[#1E3A5F] hover:bg-slate-50'
                }`}
              >
                {fylke.name}
              </button>
            ))}
          </div>
        </div>

        {/* By-velger under fylke-pills hvis fylke er valgt og har flere byer */}
        {selected && byer.length > 1 && onBySelect && (
          <div className="mt-3 px-4">
            <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
              <div className="flex gap-2 pb-2" style={{ width: 'max-content' }}>
                <span className="text-sm text-slate-500 py-2 pr-2 flex items-center gap-1">
                  <span className="material-symbols-outlined text-base">location_city</span>
                  By:
                </span>
                <button
                  onClick={() => onBySelect(null)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                    !selectedBy
                      ? 'bg-[#1E3A5F] text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  Alle byer
                </button>
                {byer.map((by) => (
                  <button
                    key={by.id}
                    onClick={() => onBySelect(by.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                      selectedBy === by.id
                        ? 'bg-[#1E3A5F] text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {by.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {locationError && (
          <p className="text-xs text-red-500 mt-2 px-4">{locationError}</p>
        )}
      </div>
    );
  }

  return (
    <div className="w-full">
      <h3 className="font-heading font-semibold text-[#0A1929] mb-3 flex items-center gap-2">
        <span className="material-symbols-outlined text-[#1E3A5F]">location_on</span>
        Velg fylke
      </h3>
      
      {showLocationButton && (
        <button
          onClick={handleUseMyLocation}
          disabled={locating}
          className={`mb-3 flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
            locating
              ? 'bg-slate-100 text-slate-400'
              : 'bg-gradient-to-r from-[#1E3A5F] to-[#0A1929] text-white shadow-md hover:shadow-lg'
          }`}
        >
          <span className={`material-symbols-outlined text-lg ${locating ? 'animate-pulse' : ''}`}>
            {locating ? 'location_searching' : 'my_location'}
          </span>
          {locating ? 'Finner posisjon...' : 'Bruk min posisjon'}
        </button>
      )}
      
      {locationError && (
        <p className="text-xs text-red-500 mb-3">{locationError}</p>
      )}
      
      <div className="flex flex-wrap gap-2">
        {showAll && (
          <button
            onClick={() => onSelect(null)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              selected === null
                ? 'bg-[#0A1929] text-white shadow-md'
                : 'bg-white text-[#0A1929] border border-slate-200 hover:border-[#1E3A5F] hover:bg-slate-50'
            }`}
          >
            Alle fylker
          </button>
        )}
        {sortedFylker.map((fylke) => (
          <button
            key={fylke.id}
            onClick={() => onSelect(fylke.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              selected === fylke.id
                ? 'bg-[#0A1929] text-white shadow-md'
                : 'bg-white text-[#0A1929] border border-slate-200 hover:border-[#1E3A5F] hover:bg-slate-50'
            }`}
          >
            {fylke.name}
          </button>
        ))}
      </div>
    </div>
  );
};

// Re-export for backwards compatibility
export { FYLKER, FYLKER_MED_BYER, getFylkeName, getByName } from '../data/norwegianCities';
export default LocationSelector;
